<template>
  <div class="container p-4">
    <h3 class="text-xl font-bold mb-3">Asignaturas</h3>
    <p class="text-sm text-600 mb-3">
      Crea o edita asignaturas del catálogo (por ejemplo <strong>SIMCE Lenguaje</strong>). El país define el contexto del registro en base de datos.
    </p>

    <CardPrime class="mb-4">
      <template #title>Búsqueda</template>
      <template #content>
        <div class="grid grid-cols-1 md:grid-cols-4 gap-3 items-end">
          <div>
            <label class="block text-sm mb-1">Glosa (contiene)</label>
            <InputText v-model="filtroGlosa" placeholder="Ej: SIMCE, Lenguaje..." class="w-full" @keyup.enter="cargarLista" />
          </div>
          <div>
            <label class="block text-sm mb-1">País (filtro)</label>
            <DropdownPrime
              v-model="filtroPaisId"
              :options="paisesOptions"
              optionLabel="Glosa"
              optionValue="Id"
              placeholder="Todos"
              class="w-full"
              showClear
            />
          </div>
          <div class="flex gap-2 flex-wrap">
            <ButtonPrime label="Buscar" icon="pi pi-search" @click="cargarLista" />
            <ButtonPrime label="Nueva asignatura" icon="pi pi-plus" class="p-button-success" @click="abrirNuevo" />
          </div>
        </div>
      </template>
    </CardPrime>

    <DataTablePrime
      :value="asignaturas"
      :loading="loading"
      class="p-datatable-sm"
      responsiveLayout="scroll"
      emptyMessage="Sin resultados"
    >
      <ColumnPrime field="Id" header="ID" />
      <ColumnPrime field="Glosa" header="Glosa" />
      <ColumnPrime header="País">
        <template #body="{ data }">{{ data.Pais?.Glosa || data.Pais?.Id || '—' }}</template>
      </ColumnPrime>
      <ColumnPrime header="Vigente">
        <template #body="{ data }">{{ data.Vigente ? 'Sí' : 'No' }}</template>
      </ColumnPrime>
      <ColumnPrime header="Acciones">
        <template #body="{ data }">
          <ButtonPrime icon="pi pi-pencil" class="p-button-sm p-mr-1" @click="abrirEditar(data)" />
          <ButtonPrime
            v-if="data.Vigente"
            label="Desactivar"
            class="p-button-sm p-button-warning"
            @click="desactivar(data)"
          />
        </template>
      </ColumnPrime>
    </DataTablePrime>

    <DialogPrime v-model:visible="dialogVisible" :modal="true" :header="dialogTitulo" :style="{ width: '480px' }">
      <div class="field mb-3">
        <label class="block mb-1">Glosa</label>
        <InputText v-model="form.Glosa" class="w-full" placeholder="Ej: SIMCE Lenguaje" />
      </div>
      <div class="field mb-3">
        <label class="block mb-1">País</label>
        <DropdownPrime
          v-model="form.PaisId"
          :options="paisesOptions"
          optionLabel="Glosa"
          optionValue="Id"
          placeholder="Selecciona país"
          class="w-full"
          :disabled="paisesOptions.length === 0"
        />
        <small v-if="paisesOptions.length === 0" class="text-600">Cargando países desde el catálogo…</small>
      </div>
      <div class="field mb-3 flex align-items-center">
        <CheckboxPrime v-model="form.Vigente" :binary="true" inputId="asig-vig" />
        <label for="asig-vig" class="ml-2">Vigente</label>
      </div>
      <template #footer>
        <ButtonPrime label="Cancelar" class="p-button-secondary" @click="dialogVisible = false" />
        <ButtonPrime label="Guardar" @click="guardar" />
      </template>
    </DialogPrime>
  </div>
</template>

<script>
import CardPrime from 'primevue/card';
import DataTablePrime from 'primevue/datatable';
import ColumnPrime from 'primevue/column';
import DialogPrime from 'primevue/dialog';
import ButtonPrime from 'primevue/button';
import InputText from 'primevue/inputtext';
import DropdownPrime from 'primevue/dropdown';
import CheckboxPrime from 'primevue/checkbox';
import AsignaturaService from '@/services/AsignaturaService';

export default {
  name: 'MantenedorAsignaturas',
  components: {
    CardPrime,
    DataTablePrime,
    ColumnPrime,
    DialogPrime,
    ButtonPrime,
    InputText,
    DropdownPrime,
    CheckboxPrime,
  },
  data() {
    return {
      asignaturas: [],
      loading: false,
      filtroGlosa: '',
      filtroPaisId: null,
      paisesOptions: [],
      dialogVisible: false,
      dialogTitulo: 'Asignatura',
      form: {
        Id: 0,
        Glosa: '',
        Vigente: true,
        PaisId: null,
      },
    };
  },
  methods: {
    async cargarPaisesDesdeCatalogo() {
      try {
        const r = await AsignaturaService.listarPaginado(1, 1000);
        const rows = r.data?.Content || r.data?.content || [];
        const map = new Map();
        rows.forEach((a) => {
          const p = a.Pais;
          if (p && p.Id) {
            const label = p.Glosa || `País ${p.Id}`;
            if (!map.has(p.Id)) map.set(p.Id, { Id: p.Id, Glosa: label });
          }
        });
        this.paisesOptions = Array.from(map.values()).sort((a, b) => a.Glosa.localeCompare(b.Glosa));
        if (this.paisesOptions.length === 0) {
          this.paisesOptions = [{ Id: 1, Glosa: 'Chile (id 1)' }];
        }
        if (this.paisesOptions.length === 1) {
          this.filtroPaisId = this.paisesOptions[0].Id;
        }
      } catch (e) {
        console.error(e);
        this.paisesOptions = [{ Id: 1, Glosa: 'Chile (id 1)' }];
      }
    },
    glosaParamBusqueda() {
      const t = (this.filtroGlosa || '').trim();
      if (!t) return null;
      return `%${t}%`;
    },
    async cargarLista() {
      this.loading = true;
      try {
        const params = { glosa: this.glosaParamBusqueda() };
        if (this.filtroPaisId) params.iPais = this.filtroPaisId;
        const r = await AsignaturaService.listar(params);
        this.asignaturas = r.data || [];
      } catch (e) {
        alert(e.response?.data?.Message || e.message || 'Error al cargar');
        this.asignaturas = [];
      } finally {
        this.loading = false;
      }
    },
    abrirNuevo() {
      this.dialogTitulo = 'Nueva asignatura';
      this.form = {
        Id: 0,
        Glosa: '',
        Vigente: true,
        PaisId: this.filtroPaisId || this.paisesOptions[0]?.Id || null,
      };
      this.dialogVisible = true;
    },
    async abrirEditar(row) {
      this.dialogTitulo = 'Editar asignatura';
      try {
        const r = await AsignaturaService.getById(row.Id);
        const a = r.data || row;
        this.form = {
          Id: a.Id,
          Glosa: a.Glosa || '',
          Vigente: a.Vigente !== false,
          PaisId: a.Pais?.Id ?? null,
        };
      } catch {
        this.form = {
          Id: row.Id,
          Glosa: row.Glosa || '',
          Vigente: !!row.Vigente,
          PaisId: row.Pais?.Id ?? null,
        };
      }
      this.dialogVisible = true;
    },
    payloadGuardar() {
      const glosa = (this.form.Glosa || '').trim();
      const idPais = Number(this.form.PaisId);
      return {
        Id: this.form.Id || 0,
        Glosa: glosa,
        Vigente: !!this.form.Vigente,
        Pais: idPais > 0 ? { Id: idPais, Glosa: null } : null,
      };
    },
    async guardar() {
      const p = this.payloadGuardar();
      if (!p.Glosa) {
        alert('Ingresa la glosa.');
        return;
      }
      if (!p.Pais || !p.Pais.Id) {
        alert('Selecciona un país.');
        return;
      }
      try {
        if (p.Id > 0) {
          await AsignaturaService.actualizar(p.Id, p);
        } else {
          await AsignaturaService.crear(p);
        }
        this.dialogVisible = false;
        await this.cargarPaisesDesdeCatalogo();
        await this.cargarLista();
      } catch (e) {
        alert(e.response?.data?.Message || e.response?.data?.message || e.message || 'Error al guardar');
      }
    },
    async desactivar(row) {
      if (!confirm(`¿Desactivar la asignatura "${row.Glosa}" (id ${row.Id})?`)) return;
      const idPais = row.Pais?.Id;
      if (!idPais) {
        alert('No se pudo determinar el país; edita la asignatura y vuelve a intentar.');
        return;
      }
      const p = {
        Id: row.Id,
        Glosa: row.Glosa,
        Vigente: false,
        Pais: { Id: idPais, Glosa: null },
      };
      try {
        await AsignaturaService.actualizar(row.Id, p);
        await this.cargarLista();
      } catch (e) {
        alert(e.response?.data?.Message || e.message || 'Error');
      }
    },
  },
  async mounted() {
    await this.cargarPaisesDesdeCatalogo();
    await this.cargarLista();
  },
};
</script>
